import { useValue } from "@legendapp/state/react";
import { normalizeSql } from "@repo/shared/helpers/normalize-sql";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Icon } from "@repo/ui/components/icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "@repo/ui/ui/avatar";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@repo/ui/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@repo/ui/ui/dropdown-menu";
import type { ReactElement } from "react";
import { toast } from "sonner";

import { DatasetRefreshMenuItem } from "~/components/dataset-refresh-menu-item";
import { DatasetSaveTooltip } from "~/components/dataset-save-tooltip";
import { downloadCSV } from "~/libs/download-csv";
import { createNamedDatasetFolder } from "~/pages/datasets/datasets.store";
import { user$ } from "~/stores/auth";
import { sendDatasetToBoard } from "~/stores/blocks/blocks.actions";
import { boards$ } from "~/stores/boards";
import {
  getSortedDatasetFolders,
  moveDatasetToFolder,
} from "~/stores/dataset-folders";
import {
  ensureRiftResult,
  useDatasetRift,
  useRiftQuery,
} from "~/stores/dataset-rifts";
import { datasets$ } from "~/stores/datasets";
import { deleteDataset, saveDataset } from "~/stores/datasets/datasets.actions";
import { members$ } from "~/stores/members";

export const DatasetOptions = ({
  datasetId,
  children,
  type = "dropdown",
  hideSave = false,
  showMoveToFolder = false,
}: {
  datasetId: string;
  children: ReactElement;
  type?: "dropdown" | "context";
  hideSave?: boolean;
  /**
   * Adds the "Move to folder" submenu. Off by default because folders only
   * exist on the datasets browse page — the sidebar list stays flat.
   */
  showMoveToFolder?: boolean;
}) => {
  const Menu = type === "dropdown" ? DropdownMenu : ContextMenu;
  const MenuTrigger =
    type === "dropdown" ? DropdownMenuTrigger : ContextMenuTrigger;
  const MenuContent =
    type === "dropdown" ? DropdownMenuContent : ContextMenuContent;
  const MenuItem = type === "dropdown" ? DropdownMenuItem : ContextMenuItem;
  const Separator =
    type === "dropdown" ? DropdownMenuSeparator : ContextMenuSeparator;
  const MenuSub = type === "dropdown" ? DropdownMenuSub : ContextMenuSub;
  const MenuSubTrigger =
    type === "dropdown" ? DropdownMenuSubTrigger : ContextMenuSubTrigger;
  const MenuSubContent =
    type === "dropdown" ? DropdownMenuSubContent : ContextMenuSubContent;

  const isPrivate = useValue(datasets$[datasetId].private);
  const isVerified = useValue(datasets$[datasetId].verified);
  const isFavorite = useValue(datasets$[datasetId].favorite);
  const isSaving = useValue(
    () => datasets$[datasetId].status.get() === "RUNNING",
  );
  const hasPendingQueryChanges = useValue(
    () =>
      normalizeSql(datasets$[datasetId].query.get()) !==
      normalizeSql(datasets$[datasetId].draftQuery.get()),
  );

  const { riftId } = useDatasetRift(datasetId);
  const hasRiftData = useRiftQuery(riftId ?? null).data != null;

  const allBoards = useValue(boards$);
  const allBoardsList = Object.values(allBoards ?? {}).filter((b) => b?.id);

  const folders = useValue(() =>
    showMoveToFolder
      ? getSortedDatasetFolders().map((folder) => ({
          id: folder.id,
          name: folder.name,
        }))
      : [],
  );
  const currentFolderId = useValue(datasets$[datasetId].folderId);

  const ownerId = useValue(datasets$[datasetId].ownerId);
  const orgMembers = useValue(() =>
    (members$.get() ?? [])
      .map((member) => ({
        userId: member.userId,
        name: member.user?.name || member.user?.email || "Unknown member",
        image: member.user?.image ?? null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  const isCreator = useValue(() => {
    const userId = user$.id.get();
    const ownerId = datasets$[datasetId].ownerId.get();
    if (!userId || !ownerId) return false;
    return userId === ownerId;
  });

  const handleDownloadCSV = async () => {
    if (!riftId) return;
    const result = await ensureRiftResult(riftId);
    if (!result?.columns || !result.data) return;
    const name = datasets$[datasetId].name.get() || "dataset";
    downloadCSV(name, result.columns, result.data);
  };

  /**
   * Files the dataset into a brand-new folder, which lands on the browse page
   * in inline-rename mode. Only reachable with `showMoveToFolder`, so the page
   * that owns that rename state is the one the menu was opened from.
   */
  const handleCreateFolder = () => {
    moveDatasetToFolder(datasetId, createNamedDatasetFolder());
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/datasets/${datasetId}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  return (
    <Menu>
      <MenuTrigger render={children} />
      <MenuContent align="end" className="w-auto min-w-32 max-w-96">
        {!hideSave && (
          <DatasetSaveTooltip
            hasPendingChanges={hasPendingQueryChanges}
            isSaving={isSaving}
            side="left"
            sideOffset={8}
          >
            <MenuItem
              onClick={() => saveDataset(datasetId)}
              disabled={!hasPendingQueryChanges || isSaving}
            >
              <Icon name="floppy-disk" className="size-4" />
              {isSaving ? "Saving..." : "Save"}
            </MenuItem>
          </DatasetSaveTooltip>
        )}
        {isCreator && (
          <MenuItem onClick={() => datasets$[datasetId].private.toggle()}>
            <Icon
              name={isPrivate ? "dataset" : "dataset-magnifying-glass"}
              className="size-4"
            />
            {isPrivate ? "Share with team" : "Make private"}
          </MenuItem>
        )}
        <MenuItem onClick={() => datasets$[datasetId].verified.toggle()}>
          <Icon
            name={isVerified ? "verified-fill" : "verified"}
            className="size-4"
          />
          {isVerified ? "Unverify" : "Verify"}
        </MenuItem>
        <MenuItem onClick={() => datasets$[datasetId].favorite.toggle()}>
          <Icon
            name={isFavorite ? "star-disabled" : "star"}
            className="size-4"
          />
          {isFavorite ? "Unfavorite" : "Favorite"}
        </MenuItem>
        {showMoveToFolder && (
          <MenuSub>
            <MenuSubTrigger>
              <Icon name={currentFolderId ? "folder-open" : "folder"} />
              Move to folder...
            </MenuSubTrigger>
            <MenuSubContent sideOffset={8} alignOffset={-4}>
              {folders.map((folder) => {
                const isCurrent = folder.id === currentFolderId;
                return (
                  <MenuItem
                    key={folder.id}
                    disabled={isCurrent}
                    onClick={() => moveDatasetToFolder(datasetId, folder.id)}
                  >
                    <Icon name={isCurrent ? "folder-open" : "folder"} />
                    {folder.name || "Untitled folder"}
                  </MenuItem>
                );
              })}
              {folders.length > 0 && <Separator />}
              <MenuItem onClick={handleCreateFolder}>
                <Icon name="plus" />
                New folder
              </MenuItem>
              {currentFolderId != null && (
                <MenuItem onClick={() => moveDatasetToFolder(datasetId, null)}>
                  <Icon name="folder-open" />
                  Remove from folder
                </MenuItem>
              )}
            </MenuSubContent>
          </MenuSub>
        )}
        <MenuSub>
          <MenuSubTrigger>
            <Icon name="person" />
            Change owner...
          </MenuSubTrigger>
          <MenuSubContent sideOffset={8} alignOffset={-4}>
            {orgMembers.length === 0 && (
              <MenuItem disabled>No members</MenuItem>
            )}
            {orgMembers.map((member) => {
              const isOwner = member.userId === ownerId;
              return (
                <MenuItem
                  key={member.userId}
                  disabled={isOwner}
                  onClick={() =>
                    datasets$[datasetId].ownerId.set(member.userId)
                  }
                >
                  <Avatar className="size-4">
                    {member.image && (
                      <AvatarImage src={member.image} alt={member.name} />
                    )}
                    <AvatarFallback className="text-[8px]">
                      {member.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="truncate">{member.name}</span>
                  {isOwner && <Icon name="check" className="ml-auto" />}
                </MenuItem>
              );
            })}
          </MenuSubContent>
        </MenuSub>
        <Separator />
        <MenuItem onClick={handleCopyLink}>
          <Icon name="link" />
          Copy link
        </MenuItem>
        {hasRiftData ? (
          <>
            <MenuItem onClick={() => void handleDownloadCSV()}>
              <Icon name="document-arrow-down" />
              Download CSV
            </MenuItem>
            <MenuSub>
              <MenuSubTrigger>
                <Icon name="dashboard" />
                Send to dashboard...
              </MenuSubTrigger>
              <MenuSubContent sideOffset={8} alignOffset={-4}>
                {allBoardsList.map((board) => (
                  <MenuItem
                    key={board.id}
                    onClick={() => sendDatasetToBoard(datasetId, board.id)}
                  >
                    <AvatarDisplay
                      avatar={board.avatar}
                      fallbackIcon="dashboard"
                    />
                    {board.name || "Untitled board"}
                  </MenuItem>
                ))}
                {allBoardsList.length === 0 && (
                  <MenuItem disabled>No boards</MenuItem>
                )}
              </MenuSubContent>
            </MenuSub>
          </>
        ) : (
          <>
            <Tooltip>
              <TooltipTrigger render={<span className="flex w-full" />}>
                <MenuItem disabled>
                  <Icon name="document-arrow-down" />
                  Download CSV
                </MenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                Run the dataset first to download results
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger render={<span className="flex w-full" />}>
                <MenuItem disabled>
                  <Icon name="dashboard" />
                  Send to dashboard...
                </MenuItem>
              </TooltipTrigger>
              <TooltipContent side="left" sideOffset={8}>
                Run the dataset first to send it to a dashboard
              </TooltipContent>
            </Tooltip>
          </>
        )}
        <DatasetRefreshMenuItem
          datasetId={datasetId}
          MenuItem={MenuItem}
          side="left"
          sideOffset={8}
        />
        <Separator />
        <MenuItem
          variant="destructive"
          onClick={() => deleteDataset(datasetId)}
        >
          <Icon name="trash" />
          Delete
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};
